import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, Input, LoadingSpinner, Select } from '@/components/common';
import { getErrorMessage } from '@/services';
import {
  REPORT_STATUS_LABEL,
  insurerReportService,
  type ReportFilters,
  type ReportStatus,
} from '@/services/insurerReport';
import { formatPeriod } from '@/services/insurerSettlements';
import { formatCurrency } from '@/utils';
import { CommissionLedgerTab } from './CommissionLedgerTab';
import { PaymentTrackingTab } from './PaymentTrackingTab';

/**
 * #359: the insurer's Commission Report (replaces the #410 earned / paid /
 * payable page). Commission is calculated from the insurer's own published
 * schedules; Paid is the payments the insurer recorded. The broker-side
 * commission screens are not the insurer's. Backed by
 * `GET /commissions/insurer-report/`.
 *
 * The dashboard card still imports these two help strings.
 */

export const PAID_HELP =
  'Paid: commission already paid out (calculations marked paid or carried by a completed payout). Click to see it by broker and policy; the Report tab counts the payments you recorded instead.';
export const PAYABLE_HELP =
  'Payable: commission earned on policies issued on your paper that has not been paid out yet (earned minus paid).';

const TH = 'px-3 py-2 text-xs font-semibold uppercase text-gray-600 dark:text-gray-300';
const TD = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100';
const STATUS_VARIANT: Record<ReportStatus, 'default' | 'info' | 'warning' | 'success'> = {
  calculated: 'default',
  approved: 'info',
  part_paid: 'warning',
  paid: 'success',
  no_rule: 'default',
};
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...(Object.keys(REPORT_STATUS_LABEL) as ReportStatus[]).map((s) => ({ value: s, label: REPORT_STATUS_LABEL[s] })),
];

export function CommissionPage() {
  // #703: the dashboard's Commission Paid / Payable cards open ?tab=ledger.
  const [params] = useSearchParams();
  const initial = params.get('tab');
  const [tab, setTab] = useState<'report' | 'payments' | 'ledger'>(
    initial === 'ledger' || initial === 'payments' ? initial : 'report'
  );
  return (
    <div className="space-y-6">
      <PageHeader
        title="Commission Report"
        subtitle="Commission due under your published schedules, and the payments you have recorded"
      />
      <div role="tablist" className="flex gap-6 border-b border-gray-200 dark:border-gray-700">
        {(
          [
            ['report', 'Report'],
            ['payments', 'Payment tracking'],
            ['ledger', 'Earned, paid and payable'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`border-b-2 px-1 py-3 text-sm font-medium ${
              tab === id
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'payments' ? <PaymentTrackingTab /> : tab === 'ledger' ? <CommissionLedgerTab /> : <ReportTab />}
    </div>
  );
}

function ReportTab() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const set = (patch: Partial<ReportFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const brokers = useQuery({ queryKey: ['insurerReportBrokers'], queryFn: insurerReportService.brokers });
  const products = useQuery({ queryKey: ['insurerReportProducts'], queryFn: insurerReportService.products });
  const report = useQuery({
    queryKey: ['insurerReport', filters, page],
    queryFn: () => insurerReportService.get({ ...filters, page }),
    placeholderData: (prev) => prev,
  });

  const download = async () => {
    setDownloading(true);
    try {
      const blob = await insurerReportService.exportCsv(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'commission_report.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const data = report.data;
  const k = data?.kpis;
  const tiles = k
    ? [
        { label: 'Calculated', value: k.calculated, help: 'Commission due under your published schedules.' },
        { label: 'Approved', value: k.approved, help: 'The part of it you have signed off month by month (see Payment tracking).' },
        { label: 'Paid', value: k.paid, help: 'Payments you have recorded for these policies. Broker payouts are not included.' },
        { label: 'Pending', value: k.pending, help: 'Calculated minus paid.' },
      ]
    : [];
  const pages = data ? Math.max(Math.ceil(data.count / data.page_size), 1) : 1;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {tiles.map((t) => (
          <Card key={t.label} className="p-4">
            <div title={t.help} className="cursor-help">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(t.value)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t.label}</p>
              <span className="sr-only">{t.help}</span>
            </div>
          </Card>
        ))}
      </div>
      {k && Number(k.on_account) > 0 && (
        <p className="text-xs text-gray-600 dark:text-gray-300">On account (paid above commission): {formatCurrency(k.on_account)}</p>
      )}

      <Card className="p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            name="report-broker"
            label="Broker"
            options={[{ value: '', label: 'All brokers' }, ...(brokers.data ?? []).map((b) => ({ value: b.id, label: b.name }))]}
            value={filters.broker ?? ''}
            onChange={(e) => set({ broker: e.target.value })}
          />
          <Select
            name="report-product"
            label="Product"
            options={[{ value: '', label: 'All products' }, ...(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))]}
            value={filters.product ?? ''}
            onChange={(e) => set({ product: e.target.value })}
          />
          <Input label="From" name="report-from" type="date" value={filters.date_from ?? ''} onChange={(e) => set({ date_from: e.target.value })} />
          <Input label="To" name="report-to" type="date" value={filters.date_to ?? ''} onChange={(e) => set({ date_to: e.target.value })} />
          <Input label="Policy number" name="report-policy" value={filters.policy ?? ''} onChange={(e) => set({ policy: e.target.value })} />
          <Select
            name="report-status"
            label="Status"
            options={STATUS_OPTIONS}
            value={filters.status ?? ''}
            onChange={(e) => set({ status: e.target.value as ReportStatus | '' })}
          />
          <div className="flex items-end">
            <Button variant="outline" onClick={download} isLoading={downloading} disabled={downloading}>
              Export CSV
            </Button>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Showing the last 12 months unless you choose dates.
          {k && k.no_rule_policies > 0 && ` ${k.no_rule_policies} policies are not in any schedule.`}
        </p>
      </Card>

      <Card className="p-4">
        {report.isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : report.isError || !data ? (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            Commission report could not be loaded: {getErrorMessage(report.error)}
          </p>
        ) : data.results.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No policies match.{' '}
            <Link to="/insurance/commission-schedules" className="text-primary-600 hover:underline dark:text-primary-400">
              Publish a commission schedule
            </Link>{' '}
            so commission can be calculated.
          </p>
        ) : (
          <>
            <ul className="space-y-3 md:hidden" aria-label="Commission report rows">
              {data.results.map((r) => (
                <li key={r.policy_id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <Link to={`/policies/${r.policy_id}`} className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
                        {r.policy_number}
                      </Link>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {r.holder_name} · {r.broker_name} · {r.product}
                      </p>
                    </div>
                    <Badge variant={STATUS_VARIANT[r.status]} size="sm">
                      {REPORT_STATUS_LABEL[r.status]}
                    </Badge>
                  </div>
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                    <dt>Premium</dt>
                    <dd className="text-right">{formatCurrency(r.premium)}</dd>
                    <dt>Commission</dt>
                    <dd className="text-right">{r.commission ? formatCurrency(r.commission) : 'Not in a schedule'}</dd>
                    <dt>Paid</dt>
                    <dd className="text-right">{formatCurrency(r.paid)}</dd>
                    <dt>Pending</dt>
                    <dd className="text-right">{r.pending ? formatCurrency(r.pending) : '—'}</dd>
                  </dl>
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/40">
                  <tr>
                    <th className={`${TH} text-left`}>Broker</th>
                    <th className={`${TH} text-left`}>Policy</th>
                    <th className={`${TH} text-left`}>Holder</th>
                    <th className={`${TH} text-left`}>Product</th>
                    <th className={`${TH} text-right`}>Premium</th>
                    <th className={`${TH} text-right`}>Commission</th>
                    <th className={`${TH} text-left`}>Commission Status</th>
                    <th className={`${TH} text-right`}>Paid Amount</th>
                    <th className={`${TH} text-right`}>Pending Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {data.results.map((r) => (
                    <tr key={r.policy_id}>
                      <td className={TD}>{r.broker_name}</td>
                      <td className={TD}>
                        <Link to={`/policies/${r.policy_id}`} className="text-blue-600 hover:underline dark:text-blue-400">
                          {r.policy_number}
                        </Link>
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{formatPeriod(r.month)}</span>
                      </td>
                      <td className={TD}>{r.holder_name}</td>
                      <td className={TD}>{r.product}</td>
                      <td className={`${TD} text-right`}>{formatCurrency(r.premium)}</td>
                      <td className={`${TD} text-right`}>{r.commission ? formatCurrency(r.commission) : 'Not in a schedule'}</td>
                      <td className={TD}>
                        <Badge variant={STATUS_VARIANT[r.status]} size="sm">
                          {REPORT_STATUS_LABEL[r.status]}
                        </Badge>
                      </td>
                      <td className={`${TD} text-right`}>{formatCurrency(r.paid)}</td>
                      <td className={`${TD} text-right`}>{r.pending ? formatCurrency(r.pending) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
              <span>
                {data.count} policies · page {data.page} of {pages}
              </span>
              <span className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </span>
            </div>
          </>
        )}
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Calculated from your published schedules; payments are those you have recorded.
        </p>
      </Card>
    </div>
  );
}
